/**
 * Date formatting that cannot throw.
 *
 * Timestamps here come from three services and an audit trail, so an absent or
 * unparseable value is realistic. Rendering an em dash beats `Invalid Date` on a
 * screen someone is using to decide whether to suspend a university, and it
 * certainly beats a thrown RangeError taking the panel down.
 */

const EM_DASH = '—';

function parse(value: string | undefined | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value: string | undefined | null, locale: string): string {
  const date = parse(value);
  if (!date) return EM_DASH;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function formatDate(value: string | undefined | null, locale: string): string {
  const date = parse(value);
  if (!date) return EM_DASH;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
}

const UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000_000],
  ['month', 2_592_000_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

/** "3 hours ago" / "in 2 days". Falls back to an em dash, never to "NaN". */
export function formatRelative(value: string | undefined | null, locale: string): string {
  const date = parse(value);
  if (!date) return EM_DASH;
  const delta = date.getTime() - Date.now();
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  for (const [unit, ms] of UNITS) {
    if (Math.abs(delta) >= ms) {
      return formatter.format(Math.round(delta / ms), unit);
    }
  }
  return formatter.format(0, 'minute');
}

/** Digit grouping for counts, so 1200 institutions is not read as 12. */
export function formatCount(value: number | undefined, locale: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return EM_DASH;
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Money for a tile or a table cell.
 *
 * COMPACT BY DEFAULT for large whole-currency amounts (XAF has no minor units in
 * practice here): "389.7M XAF" scans; "389,694,000 XAF" wraps the currency onto a
 * second line inside a tile. The exact figure is always available as `exact` for
 * a `title` tooltip or a details view — never invent a second rounding.
 *
 * A non-breaking space between the figure and the code stops the browser breaking
 * between them, which is what made "389,694,000" / "XAF" look like two labels.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined,
  locale: string,
  options: { compact?: boolean } = {},
): { display: string; exact: string } {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) {
    return { display: EM_DASH, exact: EM_DASH };
  }

  const code = currency?.trim() || '';
  const exactFigure = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(amount);
  const exact = code ? `${exactFigure}\u00A0${code}` : exactFigure;

  if (options.compact === false) {
    return { display: exact, exact };
  }

  // Compact once the figure would dominate a tile — roughly six digits and up.
  const useCompact = Math.abs(amount) >= 100_000;
  const figure = useCompact
    ? new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumSignificantDigits: 3,
      }).format(amount)
    : exactFigure;

  return {
    display: code ? `${figure}\u00A0${code}` : figure,
    exact,
  };
}
